import { Component, type ErrorInfo, type ReactNode } from 'react';

export interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error) => ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[ErrorBoundary] Caught render error:', error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error);
      }

      return (
        <div
          className="flex flex-col items-center justify-center gap-2 rounded-lg border border-[#e0e0e0] bg-white p-8 text-center"
          role="alert"
        >
          <p className="text-lg font-semibold text-[#333]">Something went wrong</p>
          <p className="text-sm text-[#666]">
            An unexpected error occurred. Please try refreshing the page.
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}
